/**
 * Lead Tracking Service (Frontend)
 * Atribuição de campanha + conversões no ganho (Meta e Google).
 */

import { supabase } from '../../lib/supabase';
import { sendPurchaseEvent } from './meta/metaEventsService';

export interface ContactTrackingAttribution {
    campaignName?: string;
    adsetName?: string;
    adName?: string;
    campaignId?: string;
    adsetId?: string;
    adId?: string;
    source?: string;
    keyword?: string;
    googleClickId?: string;
}

export type WonConversionResult = {
    meta: { success: boolean; error?: string };
    google: { success: boolean; skipped?: boolean; error?: string };
};

function errorMessage(err: unknown, fallback: string): string {
    return err instanceof Error ? err.message : fallback;
}

/** CTA do botão/título do anúncio — não é nome de campanha/conjunto/anúncio (DB/log). */
function isGenericAdCtaLabel(value?: string | null): boolean {
    if (!value?.trim()) return true;
    const raw = value.trim().toLowerCase();
    const n = raw.replace(/^[\p{Extended_Pictographic}\p{S}\s]+/u, '').trim();
    if (!n || n.length < 3) return true;
    const blocked = [
        'converse conosco', 'enviar mensagem', 'send message', 'send whatsapp message',
        'chat with us', 'fale conosco', 'saiba mais', 'learn more', 'shop now', 'compre agora',
        'clique aqui e fale agora com um dos nossos consultores!',
        'clique e fale agora com um dos nossos consultores!',
        'quero aproveitar a oferta',
    ];
    return blocked.includes(n) || blocked.includes(raw)
        || /^(converse|enviar mensagem|send message|fale conosco|saiba mais|clique aqui|clique e fale|fale agora|quero aproveitar)/i.test(n);
}

export function metaDisplayName(value?: string | null): string | undefined {
    if (isGenericAdCtaLabel(value)) return undefined;
    const trimmed = value?.trim();
    if (!trimmed || /^\d{5,}$/.test(trimmed)) return undefined;
    return trimmed;
}

/** Primeiro nome legível disponível (sem IDs / CTAs). */
export function resolveMetaOriginLabel(
    names: (string | null | undefined)[],
): string | undefined {
    for (const name of names) {
        const display = metaDisplayName(name);
        if (display) return display;
    }
    return undefined;
}

export async function enrichMetaAttribution(
    organizationId: string,
    remoteJid: string,
    contactId?: string,
    adId?: string,
): Promise<ContactTrackingAttribution | null> {
    if (!organizationId || !remoteJid) return null;

    try {
        const { data, error } = await supabase.functions.invoke('meta-api', {
            body: {
                action: 'enrich-meta-attribution',
                organizationId,
                remoteJid,
                contactId,
                ...(adId ? { adId } : {}),
            },
        });
        if (error) return null;

        return {
            campaignName: metaDisplayName(data?.campaignName),
            adsetName: metaDisplayName(data?.adsetName),
            adName: metaDisplayName(data?.adName),
        };
    } catch {
        return null;
    }
}

export async function getContactTrackingAttribution(
    organizationId: string,
    contactId: string,
): Promise<ContactTrackingAttribution | null> {
    if (!organizationId || !contactId) return null;

    try {
        const { data, error } = await supabase
            .from('lead_tracking')
            .select(
                'first_touch_source, first_touch_campaign_id, first_touch_campaign_name, first_touch_adset_id, first_touch_adset_name, first_touch_ad_id, first_touch_ad_name, first_touch_keyword',
            )
            .eq('organization_id', organizationId)
            .eq('contact_id', contactId)
            .maybeSingle();

        if (error || !data) return null;

        return {
            source: data.first_touch_source || undefined,
            campaignId: data.first_touch_campaign_id || undefined,
            campaignName: data.first_touch_campaign_name || undefined,
            adsetId: data.first_touch_adset_id || undefined,
            adsetName: data.first_touch_adset_name || undefined,
            adId: data.first_touch_ad_id || undefined,
            adName: data.first_touch_ad_name || undefined,
            keyword: data.first_touch_keyword || undefined,
        };
    } catch (error) {
        console.warn('[LeadTrackingService] Erro ao buscar atribuição:', error);
        return null;
    }
}

/** Registra valor de conversão no lead_tracking (RPC atômica, fallback update). */
async function recordConversionValueByContact(
    organizationId: string,
    contactId: string,
    value: number,
): Promise<{ success: boolean; error?: string }> {
    if (!contactId || value <= 0) {
        return { success: false, error: 'ContactId ou valor inválido' };
    }

    try {
        const { data: tracking, error: findError } = await supabase
            .from('lead_tracking')
            .select('id, total_value')
            .eq('organization_id', organizationId)
            .eq('contact_id', contactId)
            .maybeSingle();

        if (findError) {
            console.error('[LeadTrackingService] Erro ao buscar tracking:', findError);
            return { success: false, error: findError.message };
        }
        if (!tracking) {
            return { success: false, error: 'Sem tracking para este contato' };
        }

        const { error: rpcError } = await supabase.rpc('add_conversion_value', {
            p_tracking_id: tracking.id,
            p_value: value,
        });

        if (rpcError) {
            console.warn('[LeadTrackingService] RPC falhou, tentando update direto:', rpcError);
            const { error: updateError } = await supabase
                .from('lead_tracking')
                .update({ total_value: (tracking.total_value || 0) + value })
                .eq('id', tracking.id);
            if (updateError) return { success: false, error: updateError.message };
        }

        return { success: true };
    } catch (error: unknown) {
        console.error('[LeadTrackingService] Erro inesperado:', error);
        return { success: false, error: errorMessage(error, 'Erro inesperado') };
    }
}

/** Upload Purchase Google Ads quando houver atribuição. Server resolve click id. */
async function sendGooglePurchaseIfAttributed(
    leadId: string,
    value: number,
    organizationId: string,
): Promise<{ success: boolean; skipped?: boolean; error?: string }> {
    if (!leadId || !organizationId || value <= 0) {
        return { success: false, skipped: true, error: 'Parâmetros inválidos' };
    }
    try {
        const { uploadGoogleAdsConversion } = await import('./googleAdsRepository');
        return await uploadGoogleAdsConversion(organizationId, leadId, value, 'purchase');
    } catch (err) {
        return { success: false, error: errorMessage(err, 'Falha Google Ads') };
    }
}

/** Dispatcher de ganho: valor interno + Meta CAPI + Google (quando atribuído). */
export async function sendWonConversions(
    leadId: string,
    value: number,
    organizationId: string,
    contactId?: string | null,
): Promise<WonConversionResult> {
    if (contactId) {
        await recordConversionValueByContact(organizationId, contactId, value);
    }

    const [meta, google] = await Promise.all([
        sendPurchaseEvent(leadId, value, organizationId).then(
            (r) => ({ success: r.success, error: r.error }),
            (err: unknown) => ({ success: false, error: errorMessage(err, 'Meta failed') }),
        ),
        sendGooglePurchaseIfAttributed(leadId, value, organizationId),
    ]);

    return { meta, google };
}
